/**
 * The Inbox's cross-book logic, as plain functions.
 *
 * ── WHY THESE ARE NOT IN THE PAGE ──
 * The page lists every book at once, but every decision about a row -
 * confirm, discard, move - is made by the book the row is in, under that
 * book's own id. Getting that grouping wrong would send one book's ids to
 * another, which the server refuses ("does not exist") and the person reads
 * as a broken button. So the grouping is a function Node's test runner can
 * pin, the same way `overview-rows.ts` keeps the Overview's row logic out of
 * its component.
 */

export type ProposalType = 'expense' | 'deposit' | 'invoice' | 'proforma' | 'item' | 'customer' | 'vendor' | 'change';

export interface Ref {
  type: ProposalType;
  id: string;
}

/** A ref and the book it is in - which is the book the request about it goes to. */
export interface BookRef extends Ref {
  orgId: string;
}

/** The refs, grouped by the book each is in - one request per book, in the order the books first appear. */
export function groupByBook(refs: BookRef[]): Map<string, Ref[]> {
  const groups = new Map<string, Ref[]>();
  for (const { orgId, ...ref } of refs) {
    const group = groups.get(orgId) ?? [];
    group.push(ref);
    groups.set(orgId, group);
  }
  return groups;
}

/**
 * Whether a row of this kind can move to another book at all.
 *
 * An expense or a deposit is a draft of a book's MONEY, and the server
 * re-drafts it in the target. An invoice is sent from its page. A draft item
 * or customer is not of any book's money - it is a catalogue row, and the
 * catalogue is per book by construction. A change names a record in THIS
 * book; moved, it would name nothing. The server refuses all four with one
 * sentence; the page does not offer them, so the sentence is never read.
 */
export function canMove(type: ProposalType): boolean {
  return type === 'expense' || type === 'deposit';
}

/** The refs "Move to" would actually send - the movable kinds only. */
export function movable(refs: BookRef[]): BookRef[] {
  return refs.filter((ref) => canMove(ref.type));
}

/**
 * Which books "Move to" offers for a selection.
 *
 * Every book except the one the rows are ALL in - a row cannot move to where
 * it already is. When the selection spans two books, both are offered: the
 * rows already in the target are skipped by the caller, and the rest go.
 * Only the MOVABLE rows count: a selection of one deposit and three draft
 * customers is a selection of one deposit as far as the menu is concerned,
 * and a selection of nothing movable offers nothing.
 */
export function moveTargets<B extends { orgId: string }>(books: B[], selected: BookRef[]): B[] {
  const moving = movable(selected);
  if (moving.length === 0) return [];
  const sources = new Set(moving.map((ref) => ref.orgId));
  return books.filter((book) => !(sources.size === 1 && sources.has(book.orgId)));
}

/**
 * The one sentence above the list after a move: how many landed, how many
 * did not, and what was substituted on the way - said here because the row
 * it is about is now under another book's chip.
 */
export function moveNotice(
  outcome: { moved: Array<{ note: string | null }>; failed: unknown[] },
  targetName: string,
): string {
  const notes = outcome.moved.map((m) => m.note).filter((note): note is string => !!note);
  const head = outcome.failed.length
    ? `Moved ${outcome.moved.length} of ${outcome.moved.length + outcome.failed.length} to ${targetName}. ` +
      `${outcome.failed.length} could not be - the reason is beside each one.`
    : `Moved ${outcome.moved.length} to ${targetName}.`;
  return notes.length ? `${head} ${notes.join('. ')}.` : head;
}
